import { useParams } from 'react-router'
import { useFetchQuestionRubricsApproveAnalyticsQuery } from '../../../../../store/apis/analytics.api'
import { useFetchTextAssignmentSubmissionsQuery } from '../../../../../store/apis/submission.api'
import { useSelector } from 'react-redux'
import assignmentSelectors from '../../../../../store/selectors/assignment.selectors'
import userSelectors from '../../../../../store/selectors/user.selectors'
import useQueryParam from '../../../../../hooks/useQueryParam'
import { DASHBOARD_TYPE_PARAM } from '../../../../../constants/query-params'
import useRubricContext from '../../../../../hooks/useRubricContext'
import { useVersionId } from '../../../../../utils/parseURL.helper'

const useTotalReviewProgress = () => {
    const { token } = useParams()
    const versionId = useVersionId()

    // Dashboard context
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const userId = useSelector(userSelectors.id)
    const dashboardType = useQueryParam(DASHBOARD_TYPE_PARAM) || 'ta'
    const useAssigneeFilter = isOwner && dashboardType === 'reviewer'
    const assigneeParam = useAssigneeFilter ? userId : undefined

    const {
        data: analyticsPerQuestionRubric,
        isLoading: isLoadingAnalytics,
        error: errorAnalytics,
    } = useFetchQuestionRubricsApproveAnalyticsQuery({
        token,
        assignee: assigneeParam,
    })

    const { questions, isFetching: isLoadingQuestions } = useRubricContext()

    const {
        data: submissions,
        isLoading: isLoadingSubmissions,
        error: errorSubmissions,
    } = useFetchTextAssignmentSubmissionsQuery({ token, versionId })

    // Flatten all questionRubrics from all questions
    const questionRubrics = questions?.flatMap(q => 
        (q.questionRubrics || []).map(qr => ({
            ...qr,
            questionName: q.name || q.content,
        }))
    ) || []

    const isLoading = isLoadingAnalytics || isLoadingQuestions || isLoadingSubmissions
    const error = errorAnalytics || errorSubmissions

    if (!analyticsPerQuestionRubric || !questionRubrics.length || !submissions) {
        return {
            total: 0,
            reviewed: 0,
            reviewedPercentage: 0,
            statsPerQuestionRubric: [],
            isLoading,
            error,
        }
    }

    // Total number of possible answers (submissions * questionRubrics)
    const totalPossibleAnswers = submissions.length * questionRubrics.length

    // Calculate total reviewed answers across all questionRubrics
    let totalReviewedAnswers = 0

    const questionRubricStats = questionRubrics.map(questionRubric => {
        const analytics = analyticsPerQuestionRubric[questionRubric.id] || { reviewedByHuman: 0, totalSubmissions: 0 }
        const reviewedCount = Math.round((analytics.totalSubmissions * analytics.reviewedByHuman) / 100)

        totalReviewedAnswers += reviewedCount

        return {
            id: questionRubric.id,
            name: questionRubric.name || questionRubric.questionName,
            reviewedCount,
            reviewedPercentage: analytics.reviewedByHuman || 0,
            totalSubmissions: analytics.totalSubmissions || 0,
        }
    })

    const totalReviewedPercentage =
        totalPossibleAnswers > 0 ? Math.round((totalReviewedAnswers / totalPossibleAnswers) * 100) : 0

    return {
        total: totalPossibleAnswers,
        reviewed: totalReviewedAnswers,
        reviewedPercentage: totalReviewedPercentage,
        statsPerQuestionRubric: questionRubricStats,
        isLoading,
        error,
    }
}

export default useTotalReviewProgress
